import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const { register } = useAuth();
  const navigate = useNavigate();

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const passwordsDontMatch = password && confirmPassword && password !== confirmPassword;

  const validateField = (fieldName, value) => {
    const errors = { ...fieldErrors };
    
    switch (fieldName) {
      case 'name':
        if (!value) {
          errors.name = 'Name is required';
        } else if (value.trim().length < 2) {
          errors.name = 'Name must be at least 2 characters';
        } else {
          delete errors.name;
        }
        break;
      case 'email':
        if (!value) {
          errors.email = 'Email is required';
        } else if (!value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          errors.email = 'Please enter a valid email address';
        } else {
          delete errors.email;
        }
        break;
      case 'mobile':
        if (!value) {
          errors.mobile = 'Mobile number is required';
        } else if (!value.match(/^[0-9]{10}$/)) {
          errors.mobile = 'Please enter a valid 10-digit mobile number';
        } else {
          delete errors.mobile;
        }
        break;
      case 'password':
        if (!value) {
          errors.password = 'Password is required';
        } else if (value.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        } else {
          delete errors.password;
        }
        break;
      case 'confirmPassword':
        if (!value) {
          errors.confirmPassword = 'Please confirm your password';
        } else if (value !== password) {
          errors.confirmPassword = 'Passwords do not match';
        } else {
          delete errors.confirmPassword;
        }
        break;
      default:
        break;
    }
    
    setFieldErrors(errors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    validateField('name', name);
    validateField('email', email);
    validateField('mobile', mobile);
    validateField('password', password);
    validateField('confirmPassword', confirmPassword);

    // Check if there are any errors
    if (!name || !email || !mobile || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (Object.keys(fieldErrors).length > 0) {
      return;
    }

    const result = await register(name, email, mobile, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>One Mart</h1>
          {/* <h2>Register</h2> */}
          <p>Create a new account to get started.</p>
        </div>
        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message" style={{ animation: 'shake 0.3s' }}>
              ⚠️ {error}
            </div>
          )}
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                validateField('name', e.target.value);
              }}
              onBlur={(e) => validateField('name', e.target.value)}
              placeholder="Enter your full name"
              className="form-input"
            />
            {fieldErrors.name && (
              <span style={{ fontSize: '12px', color: '#dc3545', marginTop: '5px', display: 'block' }}>
                {fieldErrors.name}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                validateField('email', e.target.value);
              }}
              onBlur={(e) => validateField('email', e.target.value)}
              placeholder="Enter your email"
              className="form-input"
            />
            {fieldErrors.email && (
              <span style={{ fontSize: '12px', color: '#dc3545', marginTop: '5px', display: 'block' }}>
                {fieldErrors.email}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>Mobile Number</label>
            <input
              type="tel"
              value={mobile}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setMobile(value);
                validateField('mobile', value);
              }}
              onBlur={(e) => validateField('mobile', e.target.value)}
              placeholder="Enter your 10-digit mobile number"
              className="form-input"
              maxLength="10"
            />
            {fieldErrors.mobile && (
              <span style={{ fontSize: '12px', color: '#dc3545', marginTop: '5px', display: 'block' }}>
                {fieldErrors.mobile}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  validateField('password', e.target.value);
                  if (confirmPassword) {
                    validateField('confirmPassword', confirmPassword);
                  }
                }}
                onBlur={(e) => validateField('password', e.target.value)}
                placeholder="Enter your password"
                className="form-input form-input-with-icon"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                }}
                className="password-toggle-btn"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.password && (
              <span style={{ fontSize: '12px', color: '#dc3545', marginTop: '5px', display: 'block' }}>
                {fieldErrors.password}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>Confirm Password</label>
            <div className="password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  validateField('confirmPassword', e.target.value);
                }}
                onBlur={(e) => validateField('confirmPassword', e.target.value)}
                placeholder="Confirm your password"
                className="form-input form-input-with-icon"
                style={{ 
                  borderColor: passwordsMatch ? '#28a745' : passwordsDontMatch ? '#dc3545' : '#ddd'
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowConfirmPassword(!showConfirmPassword);
                }}
                className="password-toggle-btn"
                title={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
            {passwordsMatch && !fieldErrors.confirmPassword && (
              <span style={{ fontSize: '12px', color: '#28a745', marginTop: '5px', display: 'block' }}>
                ✓ Passwords match
              </span>
            )}
            {fieldErrors.confirmPassword && (
              <span style={{ fontSize: '12px', color: '#dc3545', marginTop: '5px', display: 'block' }}>
                {fieldErrors.confirmPassword}
              </span>
            )}
          </div>
          <button type="submit" className="btn-primary">
            Register
          </button>
        </form>
        <div className="auth-footer">
          <p>Already have an account? <Link to="/login">Login here</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Register;
